"use client"

import { useState } from "react"
import "./ResourcePanel.css"

const ResourcePanel = ({ gameState }) => {
  const [isOpen, setIsOpen] = useState(false)

  const getResourceColor = (current, max) => {
    const percentage = (current / max) * 100
    if (percentage > 60) return "#4caf50" // Green
    if (percentage > 30) return "#ff9800" // Orange
    return "#f44336" // Red
  }

  const getStatusColor = (value) => {
    if (value > 70) return "#4caf50" // Green
    if (value > 40) return "#ff9800" // Orange
    return "#f44336" // Red
  }

  const calculateSurvivalRate = () => {
    const { resources, colonyHealth, colonyMorale, population } = gameState
    const resourceAverage = ((resources.food + resources.water + resources.energy + resources.oxygen) / 4000) * 100
    const populationHealth = (population / 1200) * 100
    return Math.floor((resourceAverage + populationHealth + colonyHealth + colonyMorale) / 4)
  }

  return (
    <div className={`resource-panel ${isOpen ? "open" : ""}`}>
      <button className="resource-toggle" onClick={() => setIsOpen(!isOpen)} title="Colony Resources">
        <span className="resource-icon">📊</span>
        <span className="resource-label">RESOURCES</span>
      </button>

      {isOpen && (
        <div className="resource-panel-content">
          <div className="resource-header">
            <div className="resource-title">
              <span className="resource-avatar">📊</span>
              <div className="resource-info">
                <h3>Colony Resources</h3>
                <p>Real-time status monitoring</p>
              </div>
            </div>
            <button className="close-btn" onClick={() => setIsOpen(false)}>
              ×
            </button>
          </div>

          <div className="resources-container">
            <div className="resources-section">
              <h4>Essential Resources</h4>
              <div className="resource-item">
                <div className="resource-label-row">
                  <span className="resource-name">🍎 Food</span>
                  <span className="resource-value">{Math.floor(gameState.resources.food)}/1000</span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${(gameState.resources.food / 1000) * 100}%`,
                      backgroundColor: getResourceColor(gameState.resources.food, 1000),
                    }}
                  ></div>
                </div>
              </div>

              <div className="resource-item">
                <div className="resource-label-row">
                  <span className="resource-name">💧 Water</span>
                  <span className="resource-value">{Math.floor(gameState.resources.water)}/1000</span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${(gameState.resources.water / 1000) * 100}%`,
                      backgroundColor: getResourceColor(gameState.resources.water, 1000),
                    }}
                  ></div>
                </div>
              </div>

              <div className="resource-item">
                <div className="resource-label-row">
                  <span className="resource-name">⚡ Energy</span>
                  <span className="resource-value">{Math.floor(gameState.resources.energy)}/1000</span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${(gameState.resources.energy / 1000) * 100}%`,
                      backgroundColor: getResourceColor(gameState.resources.energy, 1000),
                    }}
                  ></div>
                </div>
              </div>

              <div className="resource-item">
                <div className="resource-label-row">
                  <span className="resource-name">🫁 Oxygen</span>
                  <span className="resource-value">{Math.floor(gameState.resources.oxygen)}/1000</span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${(gameState.resources.oxygen / 1000) * 100}%`,
                      backgroundColor: getResourceColor(gameState.resources.oxygen, 1000),
                    }}
                  ></div>
                </div>
              </div>

              <div className="resource-item">
                <div className="resource-label-row">
                  <span className="resource-name">🔧 Materials</span>
                  <span className="resource-value">{Math.floor(gameState.resources.materials)}/1000</span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${(gameState.resources.materials / 1000) * 100}%`,
                      backgroundColor: getResourceColor(gameState.resources.materials, 1000),
                    }}
                  ></div>
                </div>
              </div>

              <div className="resource-item">
                <div className="resource-label-row">
                  <span className="resource-name">🔬 Knowledge</span>
                  <span className="resource-value">{Math.floor(gameState.resources.knowledge)}/1000</span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${(gameState.resources.knowledge / 1000) * 100}%`,
                      backgroundColor: getResourceColor(gameState.resources.knowledge, 1000),
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="colony-stats-section">
              <h4>Colony Status</h4>
              <div className="stat-item">
                <div className="stat-label-row">
                  <span className="stat-name">👥 Population</span>
                  <span className="stat-value">{Math.floor(gameState.resources.population)}</span>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-label-row">
                  <span className="stat-name">❤️ Health</span>
                  <span className="stat-value" style={{ color: getStatusColor(gameState.colonyHealth) }}>
                    {Math.floor(gameState.colonyHealth)}%
                  </span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${gameState.colonyHealth}%`,
                      backgroundColor: getStatusColor(gameState.colonyHealth),
                    }}
                  ></div>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-label-row">
                  <span className="stat-name">😊 Morale</span>
                  <span className="stat-value" style={{ color: getStatusColor(gameState.colonyMorale) }}>
                    {Math.floor(gameState.colonyMorale)}%
                  </span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${gameState.colonyMorale}%`,
                      backgroundColor: getStatusColor(gameState.colonyMorale),
                    }}
                  ></div>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-label-row">
                  <span className="stat-name">🎯 Survival Rate</span>
                  <span className="stat-value" style={{ color: getStatusColor(calculateSurvivalRate()) }}>
                    {calculateSurvivalRate()}%
                  </span>
                </div>
                <div className="resource-bar">
                  <div
                    className="resource-fill"
                    style={{
                      width: `${calculateSurvivalRate()}%`,
                      backgroundColor: getStatusColor(calculateSurvivalRate()),
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ResourcePanel
